'use client'

import React, { useMemo, useState } from 'react'

import { format } from 'date-fns'
import type { DateRange, DayContentProps } from 'react-day-picker'
import { DayPicker } from 'react-day-picker'
import { HiMiniChevronLeft, HiMiniChevronRight } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

const DATE_FORMAT = 'yyyy/MM/dd'

type CalendarProps = React.ComponentProps<typeof DayPicker>

export type DateWithMessage = {
  date: Date
  message: string
}

interface DayContent extends DayContentProps {
  message?: string
}

type CalendarPropsWithTooltipDates = CalendarProps & {
  datesWithMessages?: DateWithMessage[]
  highlightToday?: boolean
}

const DayContent: React.FC<DayContent> = ({
  date,
  displayMonth,
  activeModifiers,
  message,
}) => {
  const showMessage = useMemo(() => {
    return message && message.length > 0
  }, [message])
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger>
        <span
          className={twMerge(
            'flex items-center justify-center rounded-md w-[34px] h-[34px]',
            showMessage
              ? 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-600/30'
              : '',
          )}
        >
          {date.getDate()}
        </span>
      </TooltipTrigger>
      {showMessage && (
        <TooltipContent className="max-w-60">
          <span>{message}</span>
        </TooltipContent>
      )}
    </Tooltip>
  )
}

const Calendar: React.FC<CalendarPropsWithTooltipDates> = ({
  className,
  classNames,
  showOutsideDays = true,
  month: _month,
  datesWithMessages,
  highlightToday = true,
  ...props
}) => {
  const [month, setMonth] = useState<Date>(_month || new Date())

  const dateToMessageMap = useMemo(() => {
    const map = new Map<string, string>()
    datesWithMessages?.forEach((date) => {
      map.set(format(date.date, DATE_FORMAT), date.message)
    })
    return map
  }, [datesWithMessages])

  return (
    <DayPicker
      month={month}
      onMonthChange={setMonth}
      showOutsideDays={showOutsideDays}
      className={twMerge('p-3', className)}
      classNames={{
        vhidden:
          'box-border p-0 m-0 bg-transparent border-0 appearance-none absolute !top-0 !w-[1px] !h-[1px] !p-0 !overflow-hidden !clip-rect(1px,1px,1px,1px)',
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption:
          'flex items-center justify-between pt-1 relative items-center text-primary-900 dark:text-primary-100',
        caption_dropdowns: 'relative inline-flex',
        caption_label:
          'relative z-10 inline-flex items-center m-0 px-1 whitespace-nowrap text-sm border-0 border-2 border-transparent text-primary-900 dark:text-primary-100 font-medium',
        dropdown:
          'appearance-none absolute z-20 top-0 bottom-0 left-0 w-full m-0 p-0 cursor-inherit opacity-0 border-none bg-transparent font-inherit text-inherit leading-inherit',
        dropdown_year: 'relative inline-flex items-center',
        dropdown_month: 'relative inline-flex items-center',
        dropdown_icon: 'ml-[5px]',
        nav: 'flex items-start justify-end whitespace-nowrap',
        nav_button: twMerge(
          'flex items-center justify-center rounded-md p-1 active:brightness-95 transition duration-100 hover:bg-primary-100 dark:hover:bg-primary-700',
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 focus:ring-0 focus:outline-none',
        ),
        nav_button_previous: '',
        nav_button_next: '',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex items-center justify-center',
        head_cell:
          'flex items-center justify-center text-primary-900 dark:text-primary-100 w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: 'flex items-center justify-center h-[34px] w-[36px] text-center text-primary-900 dark:text-primary-100 text-sm p-0 relative focus-within:relative focus-within:z-20',
        day: twMerge(
          'flex items-center justify-center p-1 transition duration-100 rounded-md hover:bg-primary-100 disabled:hover:bg-white dark:hover:bg-primary-700 dark:disabled:hover:bg-primary-800',
          'h-[34px] w-[36px] font-normal aria-selected:opacity-100 focus:ring-0 focus:outline-none',
        ),
        // TODO: extremely hacky but it works for now, for some reason the tailwind from the following link isn't working
        // https://github.com/shadcn-ui/ui/blob/c579e9232cc6f3395f3351cebe6d7e9b3577d8ca/apps/www/registry/default/ui/calendar.tsx
        day_disabled: 'opacity-50',
        day_range_start:
          props.mode === 'range'
            ? props.selected?.to?.toISOString() ===
              props.selected?.from?.toISOString()
              ? 'rounded-l-md rounded-r-md'
              : props.selected?.to !== undefined &&
                  props.selected?.from !== undefined
                ? 'rounded-l-md rounded-r-none hover:rounded-r-none'
                : 'rounded-md'
            : '',
        day_range_end:
          props.mode === 'range'
            ? props.selected?.from !== undefined
              ? props.selected?.to?.toISOString() ===
                  props.selected?.from?.toISOString() ||
                props.selected?.to === undefined
                ? 'rounded-r-md'
                : 'rounded-r-md rounded-l-none hover:rounded-l-none'
              : 'rounded-md'
            : '',
        day_selected: twMerge(
          'bg-primary-100 dark:bg-primary-700 text-primary-900 dark:text-primary-100 hover:rounded-md hover:bg-primary-200 dark:hover:bg-primary-600 focus:bg-primary-200 dark:focus:bg-primary-600 focus:ring-0 focus:outline-none',
          props.mode === 'single' ? 'rounded-md' : '',
        ),
        day_today: highlightToday
          ? '!font-semibold bg-primary-100 america:bg-primary-50 dark:bg-primary-900'
          : '',
        day_outside:
          'day-outside text-primary-500 dark:text-primary-400 opacity-50 aria-selected:bg-primary-100/50 aria-selected:text-primary-900 dark:aria-selected:text-primary-100',
        day_range_middle:
          'aria-selected:bg-primary-100 aria-selected:text-primary-900 rounded-none hover:rounded-none',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ ...props }) => (
          <HiMiniChevronLeft className="h-4 w-4 text-primary-900 dark:text-primary-100" />
        ),
        IconRight: ({ ...props }) => (
          <HiMiniChevronRight className="h-4 w-4 text-primary-900 dark:text-primary-100" />
        ),
        DayContent: (props) => (
          <DayContent
            {...props}
            message={dateToMessageMap.get(format(props.date, DATE_FORMAT))}
          />
        ),
      }}
      captionLayout="dropdown-buttons"
      fromYear={2000}
      toYear={2034}
      {...props}
    />
  )
}

Calendar.displayName = 'Calendar'

export type { DateRange }

export default Calendar
